import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { Offcanvas, Form } from "react-bootstrap";
import { MultiSelect } from "react-multi-select-component";
import { ApiHook } from "../../hooks/apiHook";
import SubmitButton from "../Common/buttons/SubmitButton";
import { epinCurrencyConverter } from "../../utils/epinCurrencyConversion";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CurrencyInput from "react-currency-input-field";
import DatePickerComponent from "../Common/DatePickerComponent";
import dayjs from "dayjs";

const EpinRequest = ({ show, handleClose, amounts, conversionFactor, setSelectedPending }) => {
  const { t } = useTranslation();
  const [date, setExpireDate] = useState(dayjs());
  const [formState, setFormState] = useState({
    amountCount: [],
    epinCount: "",
    expiryDate: dayjs(),
  });
  const [errorMessage, setErrorMessage] = useState({
    amountCount: null,
    epinCount: null,
    expiryDate: null,
    paymentReceipt: null,
  });
  const [isCalenderOpen, setIsCalenderOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const queryClient = useQueryClient();
  const requestMutation = ApiHook.CallEpinRequest();

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormState((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setErrorMessage((prevData) => ({
      ...prevData,
      [id]: null,
    }));
    if (value === null || value === "") {
      setErrorMessage((prev) => ({
        ...prev,
        [id]: "*Required",
      }));
    }
  };
  const handleAmountChange = (amountCount) => {
    if (amountCount?.length === 0) {
      setErrorMessage((prev) => ({
        ...prev,
        amountCount: "*Required",
      }));
    } else {
      setErrorMessage((prevData) => ({
        ...prevData,
        amountCount: null,
      }));
    }

    setFormState((prevData) => ({
      ...prevData,
      amountCount,
    }));
  };

  const handleDateChange = (newDate) => {
    if (newDate) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      setExpireDate(newDate);
      setFormState((prev) => ({
        ...prev,
        expiryDate: formattedDate,
      }));
      setErrorMessage((prev) => ({
        ...prev,
        expiryDate: null,
      }));
    }
  };
  const openCalender = () => {
    setIsCalenderOpen(true);
  };
  const closeCalender = () => {
    setIsCalenderOpen(false);
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    console.log("📎 File selected:", file);
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      console.log("🔍 File type:", file.type, "Allowed:", allowedTypes.includes(file.type));
      if (!allowedTypes.includes(file.type)) {
        console.error("❌ Invalid file type:", file.type);
        setErrorMessage((prev) => ({
          ...prev,
          paymentReceipt: t("invalidFileType"),
        }));
        return;
      }
      
      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB
      console.log("📏 File size:", file.size, "Max:", maxSize, "Valid:", file.size <= maxSize);
      if (file.size > maxSize) {
        console.error("❌ File size exceeded:", file.size);
        setErrorMessage((prev) => ({
          ...prev,
          paymentReceipt: t("fileSizeExceeded"),
        }));
        return;
      }
      
      console.log("✅ File validation passed");
      setSelectedFile(file);
      setErrorMessage((prev) => ({
        ...prev,
        paymentReceipt: null,
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
        console.log("🖼️ File preview created");
      };
      reader.readAsDataURL(file);
    }
  };
  
  const isFormValid = () => {
    return formState?.amountCount?.length > 0 && formState?.epinCount > 0 && selectedFile !== null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log("📝 Form submitted");
    console.log("Form state:", formState);
    console.log("Selected file:", selectedFile);
    
    if (!isFormValid()) {
      console.error("❌ Form validation failed");
      if (!selectedFile) {
        console.error("❌ No file selected");
        setErrorMessage((prev) => ({
          ...prev,
          paymentReceipt: t("paymentReceiptRequired"),
        }));
      }
      return;
    }
    
    const amountValues = formState.amountCount.map((option) => option.value);
    console.log("💰 Amount values:", amountValues);
    
    // Use FormData for file upload
    const formData = new FormData();
    formData.append('epinCount', formState.epinCount);
    console.log("➕ Added epinCount:", formState.epinCount);
    
    // Append each amount code
    amountValues.forEach(code => {
      formData.append('amountCode[]', code);
      console.log("➕ Added amountCode:", code);
    });
    
    // Append the payment receipt file
    if (selectedFile) {
      formData.append('file', selectedFile);
      console.log("➕ Added file:", selectedFile.name, selectedFile.type, selectedFile.size);
    }
    
    // Log all FormData entries
    console.log("📦 FormData contents:");
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}:`, value.name, value.type, value.size);
      } else {
        console.log(`  ${key}:`, value);
      }
    }
    
    console.log("🚀 Sending request...");
    requestMutation.mutateAsync(formData, {
      onSuccess: (res) => {
        console.log("✅ Request successful:", res);
        if (res.status === 200) {
          console.log("✅ Status 200 - Request completed successfully");
          setErrorMessage({
            amountCount: null,
            transactionPassword: null,
            paymentReceipt: null,
          });
          setFormState({
            amountCount: [],
            epinCount: "",
            expiryDate: dayjs(),
          });
          setExpireDate(dayjs());
          setSelectedFile(null);
          setFilePreview(null);
          setSelectedPending(true);
          queryClient.invalidateQueries({ queryKey: ["epin-tiles"] });
          queryClient.invalidateQueries({ queryKey: ["epin-lists"] });
          queryClient.invalidateQueries({ queryKey: ["epin-pending"] });
          handleClose(false);
        } else {
          console.warn("⚠️ Unexpected status:", res.status);
          if (res?.data?.code === 1014) {
            console.error("❌ Error 1014:", res?.data?.description);
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              amountCount: t(res?.data?.description),
            }));
          } else if (res?.data?.code === 1015) {
            console.error("❌ Error 1015:", res?.data?.description);
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              transactionPassword: t(res?.data?.description),
            }));
          } else if (res?.data?.code === 1045) {
            console.error("❌ Error 1045:", res?.data?.description);
            toast.error(t(res?.data?.description));
          } else if (res?.data?.code === 429) {
            console.error("❌ Error 429:", res?.data?.description);
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              amountCount: t(res?.data?.description),
            }));
          } else if (res?.data?.code === 1103) {
            console.error("❌ Error 1103:", res?.data?.description);
            setErrorMessage((prevErrors) => ({
              ...prevErrors,
              expiryDate: t(res?.data?.description),
            }));
          } else if (res?.data?.code) {
            console.error("❌ Error code:", res?.data?.code, res?.data?.description);
            toast.error(res?.data?.description);
          } else {
            console.error("❌ Error message:", res?.data?.message);
            toast.error(res?.data?.message);
          }
        }
      },
      onError: (error) => {
        console.error("❌ Request failed:", error);
        console.error("Error response:", error?.response);
        console.error("Error data:", error?.response?.data);
        console.error("Error status:", error?.response?.status);
        console.error("Error headers:", error?.response?.headers);
      },
    });
  };

  return (
    <Offcanvas show={show} onHide={() => {
      handleClose();
      setErrorMessage({
        amountCount: null,
        transactionPassword: null,
        paymentReceipt: null,
      });
      setFormState({
        amountCount: [],
        epinCount: "",
        expiryDate: dayjs(),
      });
      setSelectedFile(null);
      setFilePreview(null);
    }} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{t("epinRequest")}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("amount")}</Form.Label>
            <MultiSelect
              id="amountCount"
              options={epinCurrencyConverter(amounts, conversionFactor)}
              value={formState.amountCount}
              onChange={handleAmountChange}
              labelledBy="Select"
            />
            <div className="required_dropDown">
              {errorMessage?.amountCount ? errorMessage?.amountCount : ""}
            </div>
          </Form.Group>

          <Form.Group className="mb-3 epin-count">
            <Form.Label>{t("epinCount")}</Form.Label>
            <CurrencyInput
              name="epinCount"
              id="epinCount"
              placeholder={t("epinCount")}
              value={formState?.epinCount}
              onValueChange={(value, id) =>
                handleChange({ target: { value, id } })
              }
              required
              allowNegativeValue={false}
              allowDecimals={false}
            />
            <div className="number-field-invalid-feedback">
              {errorMessage.epinCount}
            </div>
          </Form.Group>

          {/* Payment Receipt Upload - Required */}
          <Form.Group className="mb-3">
            <Form.Label>
              {t("paymentReceipt")} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="file"
              accept="image/jpeg,image/png,image/gif"
              onChange={handleFileChange}
              required
              isInvalid={errorMessage.paymentReceipt}
            />
            <Form.Text className="text-muted">
              {t("uploadBankReceiptHint")}
            </Form.Text>
            
            {/* Preview */}
            {filePreview && (
              <div className="mt-2 text-center">
                <img 
                  src={filePreview} 
                  alt="Receipt Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '200px', 
                    objectFit: 'contain',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '5px'
                  }}
                />
              </div>
            )}
            
            {errorMessage.paymentReceipt && (
              <div className="invalid-feedback d-block">
                {errorMessage.paymentReceipt}
              </div>
            )}
          </Form.Group>

          {/* <Form.Group className="mb-3 expire-date">
            <Form.Label>{t("expiryDate")}</Form.Label>
            <DatePickerComponent
              date={date}
              handleDateChange={handleDateChange}
              isCalenderOpen={isCalenderOpen}
              openCalender={openCalender}
              closeCalender={closeCalender}
            />
            <div className="number-field-invalid-feedback">
              {errorMessage.expiryDate}
            </div>
          </Form.Group> */}
          <SubmitButton
            isSubmitting={!isFormValid() || requestMutation.isLoading}
            click={handleSubmit}
            text="epinRequest"
            isLoading={requestMutation.isLoading}
            className={"btn btn-primary submit_btn"}
          />
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default EpinRequest;
